import {uniq} from 'lodash'
import {clone, filter, join, keys, prop, head} from 'ramda'
import SwitchCompanyController from './components/switch-company/controller'
import SwitchCompanyTemplate from './components/switch-company/template.html'
import errorHandler from './shared/errorHandler'

class App {
  /* @ngInject */
  constructor ($interval, $mdDialog, $rootScope, Auth, Users, Companies, $state, $mdToast, $cookies, $window, $ocLazyLoad, moment, PermRoleStore, $mdSidenav, Message) {
    this.Auth = Auth
    this.Companies = Companies
    this.$state = $state
    this.reactAppUrl = CONFIG.REACT_APP_BASE
    this.$mdToast = $mdToast
    this.$ocLazyLoad = $ocLazyLoad
    this.$window = $window
    this.$cookies = $cookies
    this.$mdSidenav = $mdSidenav
    this.moment = moment
    this.$interval = $interval
    this.Message = Message
    this.last = {
      bottom: false,
      top: true,
      left: false,
      right: true
    }
    this.toastPosition = clone(this.last)
    this.chatOn = false
    this.Users = Users
    this.PermRoleStore = PermRoleStore
    this.loadingSemaphore = false
    this.$mdDialog = $mdDialog
    this.companyId
    this.googleMapsUrl = `https://maps.googleapis.com/maps/api/js?key=${CONFIG.GOOGLE_API_KEY}&libraries=places`
    this.tooltips = {
      jobs: 'view scheduled jobs',
      crews: 'assemble teams & crews'
    }
    this.contextMenuOpen = false
    this.lastLogId
    this.jobActivity = []
    this.showActivity = false
    // When reloading the page tab in nav bar should highlight
    // the currently selected page, not the Roster page as default
    this.mainNavRoutsNames = {
      'app.jobs': 'Scheduled-Jobs',
      'app.roster': 'Roster',
      'app.workforce.worker': 'Workforce',
      'app.analytics': 'Reports',
      'app.crews': 'Crews',
      'app.payroll': 'Timesheets',
    }
    this.currentRoutName = this.$state.current.name
    this.currentNavItem = this.mainNavRoutsNames[this.currentRoutName]
    // Global route change loading indicator.
    $rootScope.$on('app:loading:start', this.showLoader.bind(this))
    $rootScope.$on('app:loading:done', this.hideLoader.bind(this))
    $rootScope.$on('app:authenticated', () => {
      this.authenticated = true
      Auth.getDetails()
      .then(this.redirect.bind(this))
      .then(this.getCompanyInfo.bind(this))
      .then(this.getFeatureFlags.bind(this))
      .then(this.canViewTimesheets.bind(this))
    })

    if (Auth.loggedIn()) {
      $rootScope.$broadcast('app:authenticated')
    }
    
    this.bootIntercomWithJwt()
    
    this.crewChat = this.crewChat.bind(this)
    this.userChat = this.userChat.bind(this)
    this.userChatFn = this.userChat

    /**
     * @typedef Company
     * @property {number} company_id
     * @property {string} company_name
     * @property {string} url
     * @property {string} city
     * @property {string} external_id
     */

    /** @type {Company[]} */
    this.companyList = []
  }

  bootIntercomWithJwt () {
    // Boot Intercom with JWT authentication
    this.Message.getIntercomToken()
      .then(function(response) {
        const user = this.Auth.me
        if (!user || !user.intercom) {
          // If no user data yet, boot with minimal config
          this.$window.Intercom('boot', {
            app_id: CONFIG.INTERCOM_APP_ID,
            custom_launcher_selector: '.intercom',
            hide_default_launcher: true,
            intercom_user_jwt: response.jwt
          })
        } else {
          // Boot with full user data and JWT
          const intercomData = Object.assign({}, user.intercom, {
            user_id: user.id,
            email: user.username,
            intercom_user_jwt: response.jwt
          })
          this.$window.Intercom('boot', Object.assign({
            app_id: CONFIG.INTERCOM_APP_ID,
            custom_launcher_selector: '.intercom',
            hide_default_launcher: true
          }, intercomData))
        }
      }.bind(this))
      .catch(function(error) {
        errorHandler(error)
        // Fallback: boot without JWT (will fail but won't break app)
        this.$window.Intercom('boot', {
          app_id: CONFIG.INTERCOM_APP_ID,
          custom_launcher_selector: '.intercom',
          hide_default_launcher: true
        })
      }.bind(this))
  }

  async getFeatureFlags () {

    await this.Auth.splitClient.ready().then(() => {
      const v2JobPostingKey = 'new_job_posting_react'
      const showTimesheetsKey = 'show_timesheets'
      let treatments;

      if(CONFIG.SPOOF_LOCAL_SPLITS) {
        treatments = {
          workplace_cognito_migration: 'on',
        }
      } else {
        treatments = this.Auth.splitClient.getTreatments([v2JobPostingKey])
      }

      this.redirectReactApp = treatments[v2JobPostingKey] === 'on'

      let attributes = {
        company_external_id: this.company.id,
        account_type: this.Auth.me.accountType,
        company_id: this.Auth.me.companyId,
        company_name: this.Auth.me.companyName
      }

      const treatment = this.Auth.splitClient.getTreatment(showTimesheetsKey, attributes)

      this.showTimesheets = treatment === 'on'

    }).catch(() => {
      console.log('Error Initializing Split.io')
    })
  }

  updateIntercomWithJwt (user) {
    return this.Message.getIntercomToken()
      .then(function(response) {
        const intercomData = Object.assign({}, user.intercom, {
          user_id: user.id,
          email: user.username,
          intercom_user_jwt: response.jwt
        })
        this.$window.Intercom('update', intercomData)
      }.bind(this))
      .catch(function(error) {
        errorHandler(error)
        this.$window.Intercom('update', user.intercom)
      }.bind(this))
  }

  redirect (user) {
    this.user = user
    
    this.updateIntercomWithJwt(user)
    
    if (this.user.isWorker) {
      if (this.$state.is('app.login')) {
        this.$state.go('app.home', {}, { reload: true })
      }
    } else if (this.user.canPostJobs) {
      if (this.$state.is('app.login')) this.$state.go('app.roster', {}, { reload: true })
    } else if (this.$state.is('app.login')) {
      this.$state.go('app.roster', {}, { reload: true })
    }
  }

  // workers is an array containing one worker external id or an array of worker internal ids
  crewChat (workers) {
    workers = uniq(workers)
    const externalIds = encodeURIComponent(JSON.stringify(workers))
    const chatUrl = `${this.reactAppUrl}/send-message?to=${externalIds}`
    this.$window.open(chatUrl, '_blank')
  }

  // worker = user external_id
  userChat (worker) {
    this.crewChat([worker])
  }

  // For messaging a full days roster
  rosterCreateGroupChat (date, roster, filters) {
    const externalIds = roster.map(x => x.id);
    this.crewChat(externalIds)
  }

  getCompanyInfo () {
    if (this.user.isWorker || !this.user.companyName) return
    const result = this.Companies.getCompanyData()
    return result.then(async (company) => {
      this.company = company
      const { result } = await this.Companies.getMyCompanies()
      this.companyList = result.map((c) => ({
        company_id: c.companyId || "",
        company_name: c.companyName,
        url: c.companyLogoUrl,
        city: c.companyCity,
        external_id: c.companyExternalId,
      }))
    })
  }

  showSwitchCompanyModal () {
    const parentEl = document.body
    return this.$mdDialog.show({
      parent: parentEl,
      template: SwitchCompanyTemplate,
      locals: {
        company: this.company,
        companyList: this.companyList
      },
      multiple: true,
      controller: SwitchCompanyController,
      controllerAs: 'ctrl'
    }).then(companyId => {
      this.addUserToCompany(companyId, false)
    })
  }

  getDashboardUrl () {
    return `${this.reactAppUrl}/positions`
  }

  getManageUsersUrl () {
    return `${this.reactAppUrl}/manageusers`
  }

  getCompanyInfoUrl () {
    return `${this.reactAppUrl}/settings/company`
  }

  addUserToCompany (companyId, showToastMessage = true) {
    this.Companies.addUserToCompany({
      companyId: companyId,
      username: this.user.username
    })
      .then(() => {
        if (showToastMessage) {
          this.showSimpleToast('Added User to Company')
        }
        this.$window.location.reload()
      })
  }

  showLoader () {
    if (!this.loadingSemaphore) {
      this.$mdDialog.show({
        template: `
        <md-dialog style="background-color:transparent;box-shadow:none; overflow: hidden; z-index: 81;" layout-align="center center">
          <div class="loader-background" layout="column" layout-align="center center" style="z-index: 82;">
            <img class="loader-logo" src="images/bluecrew-logo.svg"/>
            <md-progress-linear md-mode="indeterminate" style="max-width:130px">
            </md-progress-linear>
          </div>
        </md-dialog>`,
        multiple: true,
        parent: document.body
      })
    }
    this.loadingSemaphore = true
  }

  hideLoader () {
    this.loadingSemaphore = false
    this.$mdDialog.hide()
  }

  getToastPosition () {
    this.sanitizePosition()
    return join(' ', filter(pos => this.toastPosition[pos], keys(this.toastPosition)))
  }

  showSimpleToast (content) {
    this.$mdToast.show(
      this.$mdToast.simple()
        .textContent(content)
        .position('bottom center')
        .hideDelay(3000))
  }

  toggleNav (navId) {
    this.$mdSidenav(navId).toggle()
  }

  setTabName (tabName) {
    this.currentNavItem = tabName
  }

  sanitizePosition () {
    var current = this.toastPosition
    if (current.bottom && this.last.top) current.top = false
    if (current.top && this.last.bottom) current.bottom = false
    if (current.right && this.last.left) current.left = false
    if (current.left && this.last.right) current.right = false
    this.last = clone(current)
  }

  logout () {
    this.$window.Intercom('shutdown') // TODO: REMOVE INTERCOM
    this.Auth.logout()
  }

  canViewTimesheets () {
    this.canViewTimesheetsProperty = this.showTimesheets && this.user.can('VIEW_TIMESHEETS')
  }
}

export default App
